import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import './AddRoute.css';

const AddRoute = () => {
  const navigate = useNavigate();
  
  // Form state
  const [formData, setFormData] = useState({
    fromCity: '',
    fromCode: '',
    toCity: '',
    toCode: '',
    airline: '',
    duration: '',
    routeType: 'domestic',
    status: 'active'
  });

  const [errors, setErrors] = useState({});

  // Placeholder airports/destinations (in real app, this would come from destinations API)
  const airports = [
    { city: 'Lagos', code: 'LOS' },
    { city: 'Abuja', code: 'ABV' },
    { city: 'Port Harcourt', code: 'PHC' },
    { city: 'Kano', code: 'KAN' },
    { city: 'London', code: 'LHR' },
    { city: 'Dubai', code: 'DXB' },
    { city: 'New York', code: 'JFK' },
    { city: 'Istanbul', code: 'IST' },
    { city: 'Accra', code: 'ACC' },
    { city: 'Addis Ababa', code: 'ADD' }
  ];

  const airlines = [
    'Air Peace',
    'British Airways',
    'Emirates',
    'Lufthansa',
    'Qatar Airways',
    'Turkish Airlines',
    'Ethiopian Airlines',
    'Arik Air',
    'Dana Air',
    'Overland Airways',
    'Ibom Air',
    'Max Air'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleFromCityChange = (e) => {
    const selectedCity = e.target.value;
    const airport = airports.find(a => a.city === selectedCity);
    if (airport) {
      setFormData(prev => ({
        ...prev,
        fromCity: airport.city,
        fromCode: airport.code
      }));
    }
  };

  const handleToCityChange = (e) => {
    const selectedCity = e.target.value;
    const airport = airports.find(a => a.city === selectedCity);
    if (airport) {
      setFormData(prev => ({
        ...prev,
        toCity: airport.city,
        toCode: airport.code
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.fromCity.trim()) {
      newErrors.fromCity = 'From airport is required';
    }
    
    if (!formData.toCity.trim()) {
      newErrors.toCity = 'To airport is required';
    }
    
    if (formData.fromCity === formData.toCity) {
      newErrors.toCity = 'To airport must be different from From airport';
    }
    
    if (!formData.airline.trim()) {
      newErrors.airline = 'Airline is required';
    }
    
    if (!formData.duration.trim()) {
      newErrors.duration = 'Flight duration is required';
    }
    
    if (!formData.routeType) {
      newErrors.routeType = 'Route type is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Placeholder for save route
      console.log('Saving route:', formData);
      // Navigate back to routes list
      navigate('/admin/routes');
    }
  };

  const handleCancel = () => {
    navigate('/admin/routes');
  };

  return (
    <AdminLayout pageTitle="Add Route">
      <div className="admin-add-route-page">
        {/* Page Title */}
        <div className="admin-add-route-header">
          <h1 className="admin-add-route-title">Add Route</h1>
          <nav className="admin-breadcrumb">
            <button 
              className="admin-breadcrumb-link"
              onClick={() => navigate('/admin/routes')}
            >
              Routes
            </button>
            <span className="admin-breadcrumb-separator">→</span>
            <span className="admin-breadcrumb-current">Add Route</span>
          </nav>
        </div>

        <form onSubmit={handleSubmit} className="admin-route-form">
          {/* Route Form Card */}
          <div className="admin-form-section">
            <div className="admin-form-section-header">
              <h2 className="admin-form-section-title">Route Information</h2>
            </div>
            <div className="admin-form-section-content">
              <div className="admin-form-grid">
                <div className="admin-form-group">
                  <label className="admin-form-label">
                    From Airport <span className="admin-required">*</span>
                  </label>
                  <select
                    name="fromCity"
                    value={formData.fromCity}
                    onChange={handleFromCityChange}
                    className={`admin-form-select ${errors.fromCity ? 'error' : ''}`}
                  >
                    <option value="">Select From Airport</option>
                    {airports.map(airport => (
                      <option key={`${airport.code}-from`} value={airport.city}>
                        {airport.city} ({airport.code})
                      </option>
                    ))}
                  </select>
                  {errors.fromCity && (
                    <span className="admin-form-error">{errors.fromCity}</span>
                  )}
                  {formData.fromCode && (
                    <span className="admin-form-hint">Code: {formData.fromCode}</span>
                  )}
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">
                    To Airport <span className="admin-required">*</span>
                  </label>
                  <select
                    name="toCity"
                    value={formData.toCity}
                    onChange={handleToCityChange}
                    className={`admin-form-select ${errors.toCity ? 'error' : ''}`}
                  >
                    <option value="">Select To Airport</option>
                    {airports
                      .filter(airport => airport.city !== formData.fromCity)
                      .map(airport => (
                        <option key={`${airport.code}-to`} value={airport.city}>
                          {airport.city} ({airport.code})
                        </option>
                      ))}
                  </select>
                  {errors.toCity && (
                    <span className="admin-form-error">{errors.toCity}</span>
                  )}
                  {formData.toCode && (
                    <span className="admin-form-hint">Code: {formData.toCode}</span>
                  )}
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">
                    Airline <span className="admin-required">*</span>
                  </label>
                  <select
                    name="airline"
                    value={formData.airline}
                    onChange={handleInputChange}
                    className={`admin-form-select ${errors.airline ? 'error' : ''}`}
                  >
                    <option value="">Select Airline</option>
                    {airlines.map(airline => (
                      <option key={airline} value={airline}>{airline}</option>
                    ))}
                  </select>
                  {errors.airline && (
                    <span className="admin-form-error">{errors.airline}</span>
                  )}
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">
                    Flight Duration <span className="admin-required">*</span>
                  </label>
                  <input
                    type="text"
                    name="duration"
                    value={formData.duration}
                    onChange={handleInputChange}
                    className={`admin-form-input ${errors.duration ? 'error' : ''}`}
                    placeholder="e.g., 6h 30m"
                  />
                  {errors.duration && (
                    <span className="admin-form-error">{errors.duration}</span>
                  )}
                  <span className="admin-form-hint">Format: Xh Ym (e.g., 6h 30m)</span>
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">
                    Route Type <span className="admin-required">*</span>
                  </label>
                  <select
                    name="routeType"
                    value={formData.routeType}
                    onChange={handleInputChange}
                    className={`admin-form-select ${errors.routeType ? 'error' : ''}`}
                  >
                    <option value="domestic">Domestic</option>
                    <option value="international">International</option>
                  </select>
                  {errors.routeType && (
                    <span className="admin-form-error">{errors.routeType}</span>
                  )}
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">
                    Status <span className="admin-required">*</span>
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="admin-form-select"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="admin-form-actions">
            <button
              type="button"
              className="admin-form-button admin-form-button-secondary"
              onClick={handleCancel}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="admin-form-button admin-form-button-primary"
            >
              Save Route
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default AddRoute;

